import { useState, useRef, useEffect } from 'react';
import { Link, NavLink } from 'react-router';
import { useAuth, useCurrentUser } from '../../hooks/useAuth';
import { Modal, Button, Input } from '../ui';
import { ShoppingCartModal } from '../shopping/ShoppingCartModal';
import { useShoppingCart } from '../../hooks/useShoppingCart';
import { useToast } from '../ui/Toast';

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [newRecipeOpen, setNewRecipeOpen] = useState(false);
  const [showShoppingCart, setShowShoppingCart] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetStep, setResetStep] = useState<'email' | 'code'>('email');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const newRecipeRef = useRef<HTMLDivElement>(null);
  const { logout, forgotPassword, resetPassword, isSendingResetCode, isResettingPassword } = useAuth();
  const { data: user } = useCurrentUser();
  const { data: cartData } = useShoppingCart();
  const { showToast } = useToast();
  const cartItemCount = cartData?.count || 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (newRecipeRef.current && !newRecipeRef.current.contains(event.target as Node)) {
        setNewRecipeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleForgotPassword = () => {
    if (!resetEmail.trim()) {
      showToast('Please enter your email', 'error');
      return;
    }
    forgotPassword(
      { email: resetEmail },
      {
        onSuccess: () => {
          setResetStep('code');
        },
      },
    );
  };

  const handleResetPassword = () => {
    if (!resetCode.trim() || !newPassword.trim()) {
      showToast('Please fill in all fields', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }
    resetPassword(
      { email: resetEmail, code: resetCode.trim(), new_password: newPassword },
      {
        onSuccess: () => {
          setShowResetModal(false);
          setResetStep('email');
          setResetEmail('');
          setResetCode('');
          setNewPassword('');
          setConfirmPassword('');
        },
      },
    );
  };

  const openResetModal = () => {
    setResetEmail(user?.email || '');
    setResetStep('email');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setShowResetModal(true);
    setMenuOpen(false);
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-inner">
          <Link to="/dashboard" className="logo">
            Recipe Keeper
          </Link>

          <nav className="nav">
            <NavLink to="/dashboard" className="nav-link">
              Recipes
            </NavLink>
            <NavLink to="/collections" className="nav-link">
              Collections
            </NavLink>
            <div className="new-recipe-menu" ref={newRecipeRef} style={{ position: 'relative' }}>
              <button
                className="btn btn-primary"
                onClick={() => setNewRecipeOpen(!newRecipeOpen)}
                aria-expanded={newRecipeOpen}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                New Recipe
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ marginLeft: '4px' }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {newRecipeOpen && (
                <div
                  className="header-dropdown active"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: 'var(--spacing-xs)',
                    minWidth: '200px',
                    zIndex: 100,
                  }}
                >
                  <Link to="/recipes/new" className="dropdown-item" onClick={() => setNewRecipeOpen(false)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Create from Scratch
                  </Link>
                  <Link to="/recipes/import" className="dropdown-item" onClick={() => setNewRecipeOpen(false)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Import from URL
                  </Link>
                </div>
              )}
            </div>
          </nav>

          {/* Hamburger menu */}
          <div className="header-menu" ref={menuRef}>
            <button
              className="hamburger-btn"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menu"
              aria-expanded={menuOpen}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>

            <div className={`header-dropdown ${menuOpen ? 'active' : ''}`}>
              <Link to="/settings" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                Settings
              </Link>
              <button
                className="dropdown-item"
                onClick={() => {
                  setShowShoppingCart(true);
                  setMenuOpen(false);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                Shopping List
                {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
              </button>
              <button className="dropdown-item" onClick={openResetModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                Reset Password
              </button>
              <button className="dropdown-item" onClick={logout}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Shopping Cart Modal */}
      <ShoppingCartModal isOpen={showShoppingCart} onClose={() => setShowShoppingCart(false)} />

      {/* Password Reset Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        title="Reset Password"
        footer={
          resetStep === 'email' ? (
            <>
              <Button variant="ghost" onClick={() => setShowResetModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleForgotPassword} isLoading={isSendingResetCode}>
                Send Code
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setResetStep('email')}>
                Back
              </Button>
              <Button variant="primary" onClick={handleResetPassword} isLoading={isResettingPassword}>
                Reset Password
              </Button>
            </>
          )
        }
      >
        {resetStep === 'email' ? (
          <>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              Enter your email address and we'll send you a code to reset your password.
            </p>
            <Input
              label="Email Address"
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
            />
          </>
        ) : (
          <>
            <p style={{ marginBottom: 'var(--spacing-md)' }}>
              Enter the code sent to <strong>{resetEmail}</strong> and your new password.
            </p>
            <Input
              label="Verification Code"
              type="text"
              value={resetCode}
              onChange={e => setResetCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              autoFocus
            />
            <Input
              label="New Password"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
            <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-xs)' }}>
              Must be at least 8 characters with uppercase, lowercase, number, and symbol.
            </p>
          </>
        )}
      </Modal>
    </header>
  );
}
